import { Component, type ErrorInfo, type ReactNode } from "react";

import { Box } from "@radix-ui/themes";
import * as Sentry from "@sentry/react";

import { WidgetErrorFallback } from "./WidgetErrorFallback";

export interface WidgetBodyErrorBoundaryProps {
  label: string;
  onReset?: () => void;
  children: ReactNode;
}

interface WidgetBodyErrorBoundaryState {
  hasError: boolean;
  resetKey: number;
}

/**
 * Class error boundary wrapping widget body children. On catch, shows
 * `WidgetErrorFallback`; "Try again" forces a remount via `resetKey` and
 * calls the optional `onReset` so a caller can recover its own state.
 */
export class WidgetBodyErrorBoundary extends Component<
  WidgetBodyErrorBoundaryProps,
  WidgetBodyErrorBoundaryState
> {
  state: WidgetBodyErrorBoundaryState = {
    hasError: false,
    resetKey: 0,
  };

  static getDerivedStateFromError(): Partial<WidgetBodyErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      tags: { component: "widget-body" },
      extra: {
        widgetLabel: this.props.label,
        componentStack: errorInfo.componentStack,
      },
    });
  }

  /**
   * A stale fallback must not survive a label change (e.g. a symbol switch
   * on the widget whose child just crashed) — without this, the fallback
   * stays mounted and its message silently relabels itself for the NEW
   * widget while still showing the OLD crash. Clearing `hasError` here
   * forces a fresh mount attempt (via the bumped `resetKey`) for the new
   * identity.
   */
  componentDidUpdate(prevProps: WidgetBodyErrorBoundaryProps) {
    if (this.state.hasError && prevProps.label !== this.props.label) {
      this.setState((s) => ({ hasError: false, resetKey: s.resetKey + 1 }));
    }
  }

  private handleRetry = () => {
    this.setState((prev) => ({
      hasError: false,
      resetKey: prev.resetKey + 1,
    }));
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <WidgetErrorFallback
          label={this.props.label}
          onRetry={this.handleRetry}
        />
      );
    }

    return (
      <Box
        key={this.state.resetKey}
        data-testid="widget-body-error-boundary-root"
        height="100%"
        minHeight="0"
      >
        {this.props.children}
      </Box>
    );
  }
}
